
import { defineComponent, computed, ref, onMounted, ComponentPublicInstance } from "vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import router from "@/router";
import store from "@/store";
import { secondsZeroPadding, translateFridgeDetailsSettingsToBuffer } from "@/utils/fridgeDetails-utils";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil from "@/utils/common-utils";
import { copyObject } from "@/utils/common-utils";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import {
  FridgeDetailsSettingsList, 
  FridgeDetailsFunctionList,
  FridgeDetailsSetSettingsList, 
  FridgeDetailsSettingListQuickOperationButtonItem,
  FridgeDetailsDisplaySettingsListItem,
  FridgeDetailsDisplayDescriptionList,
  FridgeDetailsSetSettingsListQuickOperationButtonItem,
  FRIDGE_DETAILS_COOLING_MODE,
  FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING,
  FRIDGE_DETAILS_FUNCTION_ID,
  FRIDGE_DETAILS_STRENGTH,
  FRIDGE_DETAILS_LIGHT_MODE,
  FRIDGE_DETAILS_PARTIAL_FROZEN_MODE,
  FRIDGE_DETAILS_PARTIAL_CHILLED_MODE,
  FRIDGE_DETAILS_VOLUME_MODE,
  FRIDGE_DETAILS_PARTIAL_HALF_DEFROST
} from "@/types/fridgeDetails";
import FridgeDetailsRadioButton from "@/components/fridgeDetails/FridgeDetailsRadioButton.vue";
import FridgeSettingsQuickButtonItem from "@/components/fridgeDetails/FridgeSettingsQuickButtonItem.vue";
import FridgeDetailsConfirmSettingsDialog from "@/components/fridgeDetails/FridgeDetailsConfirmSettingsDialog.vue";
import CommonSwitch from "@/components/common/CommonSwitch.vue";
import FridgeDetailsSettingsHelpDialog from "@/components/fridgeDetails/FridgeDetailsSettingsHelpDialog.vue";

export default defineComponent({
  name: "FridgeSettingsPage",
  components: {
    CommonDesignDialog,
    FridgeDetailsRadioButton,
    FridgeSettingsQuickButtonItem,
    FridgeDetailsConfirmSettingsDialog,
    CommonSwitch,
    FridgeDetailsSettingsHelpDialog
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "本体設定"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // ダイアログ情報 API実行エラーのダイアログは、割り込まれるので、API実行前に、以下の情報で判別する
    class FridgeSettingsDialogInfo {
      type: number;         // 0:なし、1:運転情報取得API実行、2:機器制御実行API実行
      negativeCallback: (() => void) | null;
      positiveCallback: (() => void) | null;
      constructor(type: number, negativeCallback: (() => void) | null, positiveCallback: (() => void) | null) {
        this.type = type;
        this.negativeCallback = negativeCallback;
        this.positiveCallback = positiveCallback;
      }
    } 
    const refAPIDialogInfo = ref(new FridgeSettingsDialogInfo(0, null, null));

    // 表示／非表示
    const isValidSettings = ref(false);
    const partialChilled = ref("partial");
    const isDetailsOpen = ref(false);
    const detailsOpenCloseButtonText = ref("");

    // 設定値保存
    const settingsBuffer = ref(new FridgeDetailsSetSettingsList());
    const quickOperationLimitList = ref(Array<FridgeDetailsSettingListQuickOperationButtonItem>());

    // 画面表示のテキスト
    const partialChilledTitleText = "パーシャル/チルド切替";
    const partialText = "パーシャル";
    const chilledText = "チルド";
    const weakText = "弱";
    const mediumText = "中";
    const strongText = "強";
    const refrigerationText = "冷蔵";
    const frozenText = "冷凍";
    const freshFrozenText = "新鮮凍結";
    const freshFrozenNoteText = "※約150分後に通常運転に戻ります。";
    const partialFrozenTitleText = "パーシャル/冷凍切替";
    const partialFrozenStrengthText = "";
    const coolingAssistRoomStrengthText = "クーリングアシストルーム";
    const quickIcingText = "速氷";
    const quickIcingNoteText = "※約6時間後に通常製氷に戻ります。";
    const stopIceMakingText = "製氷停止";
    const quickOperationButtontitleText = "クイック操作ボタン";
    const detailSettingPlusText = "詳細設定 ＋";
    const detailSettingTitleText = "詳細設定";
    const econaviLampTitleText = "「エコナビ」ランプ表示";
    const coolingAssistLampTitleText = "「クーリングアシスト」ランプ表示";
    const doorAlarmVolumeTitleText = "ドアアラーム音量";
    const innerLampTitleText = "冷蔵室庫内照明";
    const partialChilledLampTitleText = "パーシャル/チルド切替室照明";
    const offLampText = "切";
    const darkLampText = "暗";
    const brightLampText = "明";
    const volumeMediumText = "中";
    const volumeLoudText = "大";
    const detailSettingMinusText = "詳細設定 ー";
    const sendButtonText = "設定を送信する";
    const initialButtonText = "初期設定に戻す";
    const partialHalfTitleText = "パーシャル半解凍";
    const partialHalf7hourTitleText = "7時間";
    const partialHalf7hourSubTitleText = "（牛もも肉、鶏もも肉）";
    const partialHalf5hourTitleText = "5時間";
    const partialHalf5hourSubTitleText = "（サーモン、鶏むね肉）";

    const partialChilledRadioButtonLabels = [partialText, chilledText];
    const partialFrozenRadioButtonLabels = [partialText, frozenText];
    const strengthRadioButtonLabels = [weakText, mediumText, strongText];
    const brightnessRadioButtonLabels = [offLampText, darkLampText, brightLampText];
    const volumeRadioButtonLabels = [volumeMediumText, volumeLoudText];
    detailsOpenCloseButtonText.value = detailSettingPlusText;

    // スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const scrollToBottom = () => {
      NativeClient.sendDebugLog("FridgeSettingsPage scrollToBottom()");
      if (scroll.value != undefined) {
        NativeClient.sendDebugLog("FridgeSettingsPage scrollToBottom() scrollHeight = " + scroll.value.scrollHeight);
        scroll.value!.scrollTo({ top: scroll.value.scrollHeight, left: 0 });
      }
    };

    // 機能取得
    const checkFunction = (functionId: string) => {
      NativeClient.sendDebugLog("FridgeSettingsPage checkFunction()");
      const functionList: FridgeDetailsFunctionList | null = store.getters["fridgeDetails/getFunctionList"];
      NativeClient.sendDebugLog("FridgeSettingsPage checkFunction() functionList = " + JSON.stringify(functionList));
      const settings: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      NativeClient.sendDebugLog("FridgeSettingsPage checkFunction() settings = " + JSON.stringify(settings));
      if (functionList == null || settings == null) {
         return false;
      }
      let keyword = "";
      switch (functionId) {
        case FRIDGE_DETAILS_FUNCTION_ID.ECONAVILEDONOFF:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.ECONAVILEDONOFF;
          if (settings.econavi_lamp_status == undefined || settings.econavi_lamp_status == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.PCROOMLIGHTMODE:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.PCROOMLIGHTMODE;
          if (settings.pcroom_light_mode == undefined && settings.pcroom_light_mode == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM;
          if (settings.partial_freezing_room_mode == undefined && settings.partial_freezing_room_mode == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN;
          if (settings.fresh_frozen_status == undefined && settings.fresh_frozen_status == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.COOLOVEN:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.COOLOVEN;
          if (settings.cooloven_lamp_mode == undefined && settings.cooloven_lamp_mode == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON;
          if (settings.quick_button_setting_list == undefined && settings.quick_button_setting_list == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST;
          if (settings.partial_half_defrost_mode == undefined || settings.partial_half_defrost_mode == null) {
            return false;
          }
          break;
        case FRIDGE_DETAILS_FUNCTION_ID.SCROOMTEMPARATURE:
          keyword = FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON;
          if (settings.sc_room_temperature == undefined || settings.sc_room_temperature == null) {
            return false;
          }
          break;
        default:
          break;
      }
      for (const item of functionList.reizo_function_list) {
        if (item.function_id == keyword) {
          return item.function_value;
        } 
      }
      return false;
    };

    // 弱／中／強をラジオ・ボタンのindexに変換
    const calculateIndexForStength = (strength: string) => {
      let result = 0;
      switch (strength) {
        case FRIDGE_DETAILS_STRENGTH.WEAK:
          result = 0;
          break;
        case FRIDGE_DETAILS_STRENGTH.MEDIUM:
          result = 1;
          break;
        case FRIDGE_DETAILS_STRENGTH.STRONG:
          result = 2;
          break;
        default:
          break;
      }
      return result;
    };

    // 切／暗／明をラジオ・ボタンのindexに変換
    const calculateIndexForBrightness = (brightness: string) => {
      let result = 0;
      switch (brightness) {
        case FRIDGE_DETAILS_LIGHT_MODE.OFF:
          result = 0;
          break;
        case FRIDGE_DETAILS_LIGHT_MODE.DARK:
          result = 1;
          break;
        case FRIDGE_DETAILS_LIGHT_MODE.BRIGHT:
          result = 2;
          break;
        default:
          break;
      }
      return result;
    };

    // 設定値一時保存値に保存する
    const setSettingBuffer = () => {
      NativeClient.sendDebugLog("FridgeSettingsPage setSettingBuffer()");
      const fromServer: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      const functionList: FridgeDetailsFunctionList | null = store.getters["fridgeDetails/getFunctionList"];
      if (fromServer == null || functionList == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage setSettingBuffer() fromServer/functionList is null");
        return false;
      }

      const buffer = translateFridgeDetailsSettingsToBuffer(fromServer, functionList);
      const tempQuickOperationLimitList = Array<FridgeDetailsSettingListQuickOperationButtonItem>();
      if (fromServer.quick_button_setting_list != undefined) {
        for (const item of fromServer.quick_button_setting_list) {
          const limitBuffer = { 
            mode: item.mode,
            name: item.name,
            time: item.time,
            time_second: item.time_second,
            upper_limit: item.upper_limit,
            upper_limit_second: item.upper_limit_second,
            lower_limit: item.lower_limit,
            lower_limit_second: item.lower_limit_second
          }
          tempQuickOperationLimitList.push(limitBuffer);
        }
      }
      settingsBuffer.value = buffer;
      NativeClient.sendDebugLog("FridgeSettingsPage setSettingBuffer() settingsBuffer = " + JSON.stringify(settingsBuffer.value));
      quickOperationLimitList.value = tempQuickOperationLimitList;
      store.dispatch("fridgeDetails/updateSettingsBuffer", buffer)

      return true;
    };

    // パーシャル／チルド切り替え
    const isValidPartialChilledChange = computed(() => {
      NativeClient.sendDebugLog("FridgeSettingsPage isValidPartialChilledChange() start");
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM)) {
        return false;
      }
      return true;
    });
    const initialIndexPartialChilled = computed(() => {
      if (settingsBuffer.value.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        return 1;
      }
      return 0;
    });
    const partialChilledChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage partialChilledChange() index = " + index);
      if (index == 1) {
        settingsBuffer.value.partial_mode = FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED;
        if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST)) {
          settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.OFF;
        }
      } else {
        settingsBuffer.value.partial_mode = FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.PARTIAL_WEAK;
        updateDisplay();
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // パーシャルの設定
    const initialIndexPartial = computed(() => {
      if (settingsBuffer.value.partial_mode != undefined && settingsBuffer.value.partial_mode != null) {
        return calculateIndexForStength(settingsBuffer.value.partial_mode!);
      }
      return 0;
    });
    const partialChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage partialChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.partial_mode = FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.PARTIAL_WEAK;
          break;
        case 1:
          settingsBuffer.value.partial_mode = FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.PARTIAL_MEDIUM;
          break;
        case 2:
          settingsBuffer.value.partial_mode = FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.PARTIAL_STRONG;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    }

    // パーシャル半解凍
    const isValidPartialHalf = computed(() => {
      NativeClient.sendDebugLog("FridgeSettingsPage isValidPartialHalf() start");
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST)) {
        return true;
      }
      return false;
    });
    const partialHalf7HourValue = computed(() => {
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM) && settingsBuffer.value.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.OFF;
        return false;
      }
      if (settingsBuffer.value.partial_half_defrost_mode != undefined && settingsBuffer.value.partial_half_defrost_mode == FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.SEVENHOUR) {
        return true;
      }
      return false;
    });
    const partialHalf7HourInhibit = computed(() => {
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM) && settingsBuffer.value.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        return true;
      }
      const originalSettings: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      NativeClient.sendDebugLog("FridgeSettingsPage partialHalf7HourInhibit() original = " + JSON.stringify(originalSettings));
      if (originalSettings != null && originalSettings!.partial_mode != undefined && originalSettings!.partial_mode != null && originalSettings!.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        return true;
      }
      return false;
    }); 
    const changePartialHalf7Hour = (status: boolean) => {
      if (settingsBuffer.value.partial_half_defrost_mode == undefined) {
        return;
      }
      if (status) {
        settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.SEVENHOUR;
      } else {
        // OFFにした場合、パーシャル半解凍自体がOFF
        settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.OFF;
      }
    };
    const partialHalf5HourValue = computed(() => {
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM) && settingsBuffer.value.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.OFF;
        return false;
      }
      if (settingsBuffer.value.partial_half_defrost_mode != undefined && settingsBuffer.value.partial_half_defrost_mode == FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.FIVEHOUR) {
        return true;
      }
      return false;
    });
    const partialHalf5HourInhibit = computed(() => {
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM) && settingsBuffer.value.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        return true;
      }
      const originalSettings: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      NativeClient.sendDebugLog("FridgeSettingsPage partialHalf7HourInhibit() original = " + JSON.stringify(originalSettings));
      if (originalSettings != null && originalSettings!.partial_mode != undefined && originalSettings!.partial_mode != null && originalSettings!.partial_mode == FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED) {
        return true;
      }
      return false;
    }); 
    const changePartialHalf5Hour = (status: boolean) => {
      if (settingsBuffer.value.partial_half_defrost_mode == undefined) {
        return;
      }
      if (status) {
        settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.FIVEHOUR;
      } else {
        // OFFにした場合、パーシャル半解凍自体がOFF
        settingsBuffer.value.partial_half_defrost_mode = FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.OFF;
      }
    };

    // 冷蔵
    const initialIndexRefrigeration = computed(() => {
      return calculateIndexForStength(settingsBuffer.value.cold_room_mode!);
    });
    const refrigerationChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage refrigerationChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.cold_room_mode = FRIDGE_DETAILS_STRENGTH.WEAK;
          break;
        case 1:
          settingsBuffer.value.cold_room_mode = FRIDGE_DETAILS_STRENGTH.MEDIUM;
          break;
        case 2:
          settingsBuffer.value.cold_room_mode = FRIDGE_DETAILS_STRENGTH.STRONG;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // 冷凍
    const initialIndexFrozen = computed(() => {
      return calculateIndexForStength(settingsBuffer.value.freezing_room_mode!);
    });
    const frozenChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage frozenChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.freezing_room_mode = FRIDGE_DETAILS_STRENGTH.WEAK;
          break;
        case 1:
          settingsBuffer.value.freezing_room_mode = FRIDGE_DETAILS_STRENGTH.MEDIUM;
          break;
        case 2:
          settingsBuffer.value.freezing_room_mode = FRIDGE_DETAILS_STRENGTH.STRONG;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // 新鮮凍結
    const isValidFreshFrozen = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN)) {
        return true;
      }
      return false;
    });
    const changeFreshFrozen = (status: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage changeFreshFrozen()");
      settingsBuffer.value.fresh_frozen_status = status;
      if (status) {
        settingsBuffer.value.fast_ice_status = false;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // クーリングアシストルーム温度
    const isValidCoolingAssistStrength = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.SCROOMTEMPARATURE)) {
        return true;
      }
      return false;
    });
    const initialIndexCoolingAssistStrength = computed(() => {
      return calculateIndexForStength(settingsBuffer.value.sc_room_temperature!);
    });
    const coolongAssistStrengthChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage coolongAssistStrengthChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.sc_room_temperature = FRIDGE_DETAILS_STRENGTH.WEAK;
          break;
        case 1:
          settingsBuffer.value.sc_room_temperature = FRIDGE_DETAILS_STRENGTH.MEDIUM;
          break;
        case 2:
          settingsBuffer.value.sc_room_temperature = FRIDGE_DETAILS_STRENGTH.STRONG;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // パーシャル・冷凍切替
    const isValidPartialFrozenChange = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM)) {
        return true;
      }
      return false;
    });
    const getPartialFrozenModeIndex = () => {      
      let retValue = 0;
      switch (settingsBuffer.value.partial_freezing_room_mode) {
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_WEAK:
          retValue = 1;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_MEDIUM:
          retValue = 1;
          break
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_STRONG:
          retValue = 1;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_WEAK:
          retValue = 0;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_MEDIUM:
          retValue = 0;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_STRONG:
          retValue = 0;
          break;
        default:
          break;
      }
      return retValue;
    };
    const getPartialFrozenStrengthIndex = () => {
      let retValue = 0;
      switch (settingsBuffer.value.partial_freezing_room_mode) {
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_WEAK:
          retValue = 0;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_MEDIUM:
          retValue = 1;
          break
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_STRONG:
          retValue = 2;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_WEAK:
          retValue = 0;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_MEDIUM:
          retValue = 1;
          break;
        case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_STRONG:
          retValue = 2;
          break;
        default:
          break;
      }
      return retValue;
    };
    const partialFrozenModeChange = (index: number) => {
      const currentPartialFrozen = getPartialFrozenModeIndex();
      if (currentPartialFrozen == index) {
        return;
      }
      const currentStrength = getPartialFrozenStrengthIndex();
      if (index == 0) {
        // パーシャル
        // パーシャル
        switch (currentStrength) {
          case 0:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_WEAK;
            break;
          case 1:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_MEDIUM;
            break;
          case 2:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_STRONG;
            break;
          default:
            break;
        }
      } else {
        // 冷凍
        switch (currentStrength) {
          case 0:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_WEAK;
            break;
          case 1:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_MEDIUM;
            break;
          case 2:
          settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_STRONG;
            break;
          default:
            break;
        }
      }
    };
    const partailFrozenStrengthChange = (index: number) => {
      const currentStrength = getPartialFrozenStrengthIndex();
      if (index == currentStrength) {
        return;
      }
      const currentMode = getPartialFrozenModeIndex();
      if (currentMode == 0) {
        // パーシャル
        switch (index) {
          case 0:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_WEAK;
            break;
          case 1:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_MEDIUM;
            break;
          case 2:
          settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_STRONG;
            break;
          default:
            break;
        }
      } else {
        // 冷凍
        switch (index) {
          case 0:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_WEAK;
            break;
          case 1:
            settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_MEDIUM;
            break;
          case 2:
          settingsBuffer.value.partial_freezing_room_mode = FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_STRONG;
            break;
          default:
            break;
        }
      }
    };
    const initialIndexPartialFrozen = computed(() => {
      return getPartialFrozenModeIndex();
    });
    const initialIndexPartialFrozenStrength = computed(() => {
      return getPartialFrozenStrengthIndex();
    });

    // エコナビ・ランプ
    const isValidEconaviLamp = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.ECONAVILEDONOFF)) {
        return true;
      }
      return false;
    });

    // クーリング・アシスト・ランプ
    const isValidCoolingAssitsLamp = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.COOLOVEN)) {
        return true;
      }
      return false;
    });
    const initialIndexCoolingAssistLamp = computed(() => {
      if (settingsBuffer.value.cooloven_lamp_mode == undefined) {
        return 0;
      }
      return calculateIndexForBrightness(settingsBuffer.value.cooloven_lamp_mode);
    });
    const coolingAssistLampChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage coolingAssistLampChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.cooloven_lamp_mode = FRIDGE_DETAILS_LIGHT_MODE.OFF;
          break;
        case 1:
          settingsBuffer.value.cooloven_lamp_mode = FRIDGE_DETAILS_LIGHT_MODE.DARK;
          break;
        case 2:
          settingsBuffer.value.cooloven_lamp_mode = FRIDGE_DETAILS_LIGHT_MODE.BRIGHT;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // ドア・アラーム
    const initialIndexDoorAlarmVolume = computed(() => {
      let result = 0;
      switch (settingsBuffer.value.door_alarms_mode) {
        case FRIDGE_DETAILS_VOLUME_MODE.MEDIUM:
          result = 0;
          break;
        case FRIDGE_DETAILS_VOLUME_MODE.BIG:
          result = 1;
          break;
        default:
          break;
      }
      return result;
    });
    const doorAlarmVolumeChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage doorAlarmVolumeChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.door_alarms_mode = FRIDGE_DETAILS_VOLUME_MODE.MEDIUM;
          break;
        case 1:
          settingsBuffer.value.door_alarms_mode = FRIDGE_DETAILS_VOLUME_MODE.BIG;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // 冷蔵室内照明
    const initialIndexInnerLamp = computed(() => {
      return calculateIndexForBrightness(settingsBuffer.value.coldroom_light_mode!);
    });
    const innerLampChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage innerLampChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.coldroom_light_mode = FRIDGE_DETAILS_LIGHT_MODE.OFF;
          break;
        case 1:
          settingsBuffer.value.coldroom_light_mode = FRIDGE_DETAILS_LIGHT_MODE.DARK;
          break;
        case 2:
          settingsBuffer.value.coldroom_light_mode = FRIDGE_DETAILS_LIGHT_MODE.BRIGHT;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // 冷蔵室内照明
    const isValidPartialChilledRoomLamp = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PCROOMLIGHTMODE)) {
        return true;
      }
      return false;
    });
    const initialIndexPartialChilledLamp = computed(() => {
      if (settingsBuffer.value.pcroom_light_mode == undefined) {
        return 0;
      }
      return calculateIndexForBrightness(settingsBuffer.value.pcroom_light_mode);
    });
    const partialChilledLampChange = (index: number) => {
      NativeClient.sendDebugLog("FridgeSettingsPage partialChilledLampChange() index = " + index);
      switch (index) {
        case 0:
          settingsBuffer.value.pcroom_light_mode = FRIDGE_DETAILS_LIGHT_MODE.OFF;
          break;
        case 1:
          settingsBuffer.value.pcroom_light_mode = FRIDGE_DETAILS_LIGHT_MODE.DARK;
          break;
        case 2:
          settingsBuffer.value.pcroom_light_mode = FRIDGE_DETAILS_LIGHT_MODE.BRIGHT;
          break;
        default:
          break;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // クイック・ボタン
    interface FridgeSettingsQuickButtonSettings {
      mode: string;
      name: string;
      minutes: number;
      seconds: number | null;
    }
    const isValidQuickButton = computed(() => {
      NativeClient.sendDebugLog("FridgeSettingsPage isValidQuickButton() start");
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON) && settingsBuffer.value.quick_button_setting_list != undefined) {
        if (settingsBuffer.value.quick_button_setting_list.length == 0) {
          NativeClient.sendDebugLog("FridgeSettingsPage isValidQuickButton() false(length)");
          return false;
        }
        NativeClient.sendDebugLog("FridgeSettingsPage isValidQuickButton() true");
        return true;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage isValidQuickButton() false(no match)");
      return false;
    });
    const quickButtonSettings = computed(() => {
      NativeClient.sendDebugLog("FridgeSettingsPage quickButtonSettings()");
      const buffer: FridgeDetailsSetSettingsList | null = store.getters["fridgeDetails/getSettingsBuffer"];
      if (buffer == null || buffer.quick_button_setting_list == undefined || buffer.quick_button_setting_list.length == 0) {
        return [];
      }
      const result: Array<FridgeSettingsQuickButtonSettings> = [];
      for (const item of buffer.quick_button_setting_list) {
        let name = item.name;
        if (name.length == 0) {
          name = "名前を設定できます";
        }
        const element: FridgeSettingsQuickButtonSettings = { mode: item.mode, name: name, minutes: item.time, seconds: item.time_second };
        result.push(element);
      }
      return result;
    });

    // スイッチ関連の操作

    const changeQuickIcing = (status: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage changeQuickIcing()");
      settingsBuffer.value.fast_ice_status = status;
      if (status) {
        settingsBuffer.value.fresh_frozen_status = false;
        settingsBuffer.value.stop_ice_status = false;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };
    const changeStopIce = (status: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage changeStopIce()");
      settingsBuffer.value.stop_ice_status = status;
      if (status) {
        settingsBuffer.value.fast_ice_status = false;
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };
    const changeEconaviLamp = (status: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage changeEconaviLamp()");
      settingsBuffer.value.econavi_lamp_status = status;
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value);
    };

    // 詳細設定の開閉
    const clickDetailsOpenClose = () => {
      isDetailsOpen.value = !isDetailsOpen.value;
      if (isDetailsOpen.value) {
        detailsOpenCloseButtonText.value = detailSettingMinusText;
        window.setTimeout(() => {
          scrollToBottom();
        }, 100)
      } else {
        detailsOpenCloseButtonText.value = detailSettingPlusText;
      }
    }

    //
    // 送信データの整形（無効な項目をundefinedに設定）
    //
    const arrangeSettingData = (setting: FridgeDetailsSetSettingsList) => {
      NativeClient.sendDebugLog("FridgeSettingsPage shapeSendData()");
      const result = copyObject(setting);
      NativeClient.sendDebugLog("FridgeSettingsPage arrangeSettingData() start result = " + JSON.stringify(result));
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.ECONAVILEDONOFF)) {
        result.econavi_lamp_status = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PCROOMLIGHTMODE)) {
        result.pcroom_light_mode = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM)) {
        result.partial_freezing_room_mode = undefined;
      } else {
        result.partial_mode = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN)) {
        result.fresh_frozen_status = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.COOLOVEN)) {
        result.cooloven_lamp_mode = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON)) {
        result.quick_button_setting_list = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST)) {
        result.partial_half_defrost_mode = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.SCROOMTEMPARATURE)) {
        result.sc_room_temperature = undefined;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage arrangeSettingData() result = " + JSON.stringify(result));
      return result;
    };

    // 設定完了ダイアログ
    const isShowCompletion = ref(false);
    const completionDialogType = ref(12);
    const completionDialogTitle = "設定完了しました。"
    const completionDialogMessage = "";
    const completionDialogButtonTitle = "閉じる";
    const completionCloseButtonHandler = () => {
      isShowCompletion.value = false;

      // 家電TOPに更新要求
      NativeClient.updateApplianceTop();

      // 家電トップに戻る
      location.href = "native-app://page-close";
    };

    //
    // API実行
    //
    const getInitialValues = async () => {
      NativeClient.sendDebugLog("FridgeSettingsPage getInitialValues()");
      const descriptionList: FridgeDetailsDisplayDescriptionList | null = store.getters["fridgeDetails/getDisplayDescriptionList"];
      if (descriptionList == null) {
        // 読み込まれていないならば、読み込む
        store.dispatch("common/startLoading");
        await store.dispatch("fridgeDetails/getSettingsInitialValue");
        store.dispatch("common/stopLoading");
      }
    };
    const getSettings = async () => {
      NativeClient.sendDebugLog("FridgeSettingsPage getSettings()");
      const buffer: FridgeDetailsSetSettingsList | null = store.getters["fridgeDetails/getSettingsBuffer"];
      if (buffer == null) {
        // 設定値一時保存がない場合は、サーバーから取得する
        refAPIDialogInfo.value = new FridgeSettingsDialogInfo(1, null, getSettings);
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        await Promise.all([
          store.dispatch("fridgeDetails/getSettings"),
          store.dispatch("fridgeDetails/getOperationSettings"),
          store.dispatch("fridgeDetails/getFunctionList")
        ]);
        NativeClient.sendDebugLog("FridgeSettingsPage getSettings() complete");
        const result = setSettingBuffer();
        isValidSettings.value = result;
        store.dispatch("common/stopLoading");
      } else {
        settingsBuffer.value = buffer;
        NativeClient.sendDebugLog("FridgeSettingsPage getSettings() settingsBuffer = " + JSON.stringify(settingsBuffer.value));
        isValidSettings.value = true;
      }
      getInitialValues();
      updateDisplay();
    };
    const selectChangeValues = (currentSettings: FridgeDetailsSetSettingsList) => {
      NativeClient.sendDebugLog("FridgeSettingsPage selectChangeValues() current = " + JSON.stringify(currentSettings));
      const oldSettings: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      if (oldSettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage selectChangeValues() oldSettings is null");
        return { };
      }
      const functionList: FridgeDetailsFunctionList | null = store.getters["fridgeDetails/getFunctionList"];
      if (functionList == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage selectChangeValues() functionList is null");
        return { };
      }
      const result = copyObject(currentSettings);
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.ECONAVILEDONOFF) || currentSettings.econavi_lamp_status == oldSettings!.econavi_lamp_status) {
        result.econavi_lamp_status = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PCROOMLIGHTMODE) || currentSettings.pcroom_light_mode == oldSettings!.pcroom_light_mode) {
        result.pcroom_light_mode = undefined;
      }
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM)) {
        result.partial_mode = undefined;
        if (currentSettings.partial_freezing_room_mode == oldSettings!.partial_freezing_room_mode) {
          result.partial_freezing_room_mode = undefined;
        }
      } else {
        // パーシャル/チルド切替有効
        result.partial_freezing_room_mode = undefined;
        if (currentSettings.partial_mode == oldSettings!.partial_mode) {
          NativeClient.sendDebugLog("FridgeSettingsPage selectChangeValues() partial_mode");
          result.partial_mode = undefined;
        }
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN) || currentSettings.fresh_frozen_status == oldSettings!.fresh_frozen_status) {
        result.fresh_frozen_status = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.COOLOVEN) || currentSettings.cooloven_lamp_mode == oldSettings!.cooloven_lamp_mode) {
        result.cooloven_lamp_mode = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON)) {
        result.quick_button_setting_list = undefined;
      } else {
        if (currentSettings.quick_button_setting_list == undefined) {
          result.quick_button_setting_list = undefined;
        } else {
          const buttonSettings: Array<FridgeDetailsSetSettingsListQuickOperationButtonItem> = []
          let isChange = false;
          for (const item of currentSettings.quick_button_setting_list) {
            const resultItem = copyObject(item);
            buttonSettings.push(resultItem);
            const oldItem = oldSettings.quick_button_setting_list.find((element) => element.mode == item.mode);
            buttonSettings.push(resultItem);
            if (oldItem != undefined && (oldItem.name != item.name || oldItem.time != item.time || oldItem.time_second != item.time_second)) {
              isChange = true;
            }
          }
          if (isChange) {
            result.quick_button_setting_list = buttonSettings;
          } else {
            result.quick_button_setting_list = undefined;
          }
        }
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST) || currentSettings.partial_half_defrost_mode == oldSettings!.partial_half_defrost_mode) {
        result.partial_half_defrost_mode = undefined;
      }
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.SCROOMTEMPARATURE) || currentSettings.sc_room_temperature == oldSettings!.sc_room_temperature) {
        result.sc_room_temperature = undefined;
      }

      // 全機種共通のパラメータ
      if (currentSettings.cold_room_mode == oldSettings.cold_room_mode) {
        result.cold_room_mode = undefined;
      }
      if (currentSettings.freezing_room_mode == oldSettings.freezing_room_mode) {
        result.freezing_room_mode = undefined;
      }
      if (currentSettings.fast_ice_status == oldSettings.fast_ice_status) {
        result.fast_ice_status = undefined;
      }
      if (currentSettings.stop_ice_status == oldSettings.stop_ice_status) {
        result.stop_ice_status = undefined;
      }
      if (currentSettings.door_alarms_mode == oldSettings.door_alarms_mode) {
        result.door_alarms_mode = undefined;
      }
      if (currentSettings.coldroom_light_mode == oldSettings.coldroom_light_mode) {
        result.coldroom_light_mode = undefined;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage selectChangeValues() complete current = " + JSON.stringify(currentSettings));
      return result;
    };
    const registSettings = async (isInitial: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage registSettings()isInitial = " + isInitial);
      let currentSettings: FridgeDetailsSetSettingsList | null = null;
      if (isInitial) {
        currentSettings = store.getters["fridgeDetails/getSettingsInitialValue"];
      } else {
        currentSettings = copyObject(settingsBuffer.value);
      }

      if (currentSettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage registSettings() currentSettings is null");
        return;
      }
      const sendData = selectChangeValues(currentSettings);
      NativeClient.sendDebugLog("FridgeSettingsPage registSettings() after selectChangeValues result = " + JSON.stringify(sendData));
      if (JSON.stringify(sendData) === "{}") {
        NativeClient.sendDebugLog("FridgeSettingsPage registSettings() sendData is null");
        return;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage registSettings() sendData = " + JSON.stringify(sendData));
      refAPIDialogInfo.value = new FridgeSettingsDialogInfo(2, null, null);
      store.dispatch("common/startFullscreenLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/registSettings", sendData);
      store.dispatch("common/stopFullscreenLoading");

      // 表示するために、バッファ更新
      setSettingBuffer();

      // 画面更新
      updateDisplay();

      // 完了ダイアログを出す。
      isShowCompletion.value = true;
    };

    const clickQuick = (mode: string) => {
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingsBuffer.value)
      router.push(`/fridgeSettingsTime?mode=` + mode);
    };

    // 表示の制御
    //let isDisplayed = false;
    onMounted(() => {
      NativeClient.sendDebugLog("FridgeSettingsPage onMounted()");

    // ページIDは未決定
    // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
    // NativeClient.sendViewedLog(logInfo);

      getSettings();
    });

    //
    // 送信前のチェック
    //
    
    // 設定のチェック 戻り値0：無効、戻り値1:ダイアログ表示不要、戻り値2:温度設定確認ダイアログを表示、戻り値3:停電そなえモードOFFの注意喚起ダイアログ表示
    const checkSettings = (currentSettings: FridgeDetailsSetSettingsList) => {
      NativeClient.sendDebugLog("FridgeSettingsPage checkSettings()");
      const originalSettings: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      NativeClient.sendDebugLog("FridgeSettingsPage checkSettings() original = " + JSON.stringify(originalSettings));
      NativeClient.sendDebugLog("FridgeSettingsPage checkSettings() current = " + JSON.stringify(currentSettings));
      if (originalSettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage checkSettings() originalSettings is null");
        return 0;
      }
      const partialFrozenFunctionEnable = checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM);
      const freshFrozenFuntionEnable = checkFunction(FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN);

      // パーシャルor冷蔵or冷凍の温度設定(弱中強)、または、パーシャル/冷凍切替、または、パーシャル/チルド切替が変更された？
      if (
        (currentSettings.cold_room_mode != originalSettings.cold_room_mode) ||
        (currentSettings.freezing_room_mode != originalSettings.freezing_room_mode) ||
        (partialFrozenFunctionEnable && currentSettings.partial_freezing_room_mode != originalSettings.partial_freezing_room_mode) ||
        (!partialFrozenFunctionEnable && currentSettings.partial_mode != originalSettings.partial_mode) 
      ) {
        // 停電そなえモード中？
        if (originalSettings.outage_prepare_status) {
          // 注意ダイアログ表示
          return 2;
        }
      } else if (
          (!originalSettings.fast_ice_status && currentSettings.fast_ice_status) ||
          (freshFrozenFuntionEnable && !originalSettings.fresh_frozen_status && currentSettings.fresh_frozen_status)
      ) {
        // 停電そなえモード中？
        if (originalSettings.outage_prepare_status) {
          // 注意ダイアログ表示
          return 3;
        }
      }
      return 1;
    };

    //
    // 「送信」と「初期に戻す」関連
    //
    const isShowConfirm = ref(false);
    const confirmTitle = ref("");
    const confirmItemList = ref(Array<FridgeDetailsDisplaySettingsListItem>());
    const confirmQuickButtonItemList = ref(Array<FridgeDetailsDisplaySettingsListItem>());
    const confirmAfterQuickButtonItemList = ref(Array<FridgeDetailsDisplaySettingsListItem>());
    const confirmInitial = ref(false);
    const clickConfirmCancel = (isInitial: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage clickConfirmCancel()");
      isShowConfirm.value = false;
    };
    const clickConfirmSend = (isInitial: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage clickConfirmSend() isInitial = " + isInitial);
      isShowConfirm.value = false;
      registSettings(isInitial);
    };

    // 設定値一覧生成
    const createSettingItemValueStrength = (itemValue: string) => {
      let retValue = "";
      switch (itemValue) {
        case FRIDGE_DETAILS_STRENGTH.WEAK:
          retValue = "弱";
          break;
        case FRIDGE_DETAILS_STRENGTH.MEDIUM:
          retValue = "中";
          break;
        case FRIDGE_DETAILS_STRENGTH.STRONG:
          retValue = "強";
          break;
      }
      return retValue;
    };
    const createSettingItemValueOnOff = (itemValue: boolean) => {
      let retValue = "切";
      if (itemValue) {
        retValue = "入";
      }
      return retValue;
    };
    const createSettingItemBrightness = (itemValue: string) => {
      let retValue = "切";
      switch (itemValue) {
        case FRIDGE_DETAILS_LIGHT_MODE.OFF:
          retValue = "切";
          break;
        case FRIDGE_DETAILS_LIGHT_MODE.DARK:
          retValue = "暗";
          break;
        case FRIDGE_DETAILS_LIGHT_MODE.BRIGHT:
          retValue = "明";
          break;
        default:
          break;
      }
      return retValue;
    };
    const createSettingItemVolume = (itemValue: string) => {
      let retValue = "";
      switch (itemValue) {
        case FRIDGE_DETAILS_VOLUME_MODE.MEDIUM:
          retValue = "中";
          break;
        case FRIDGE_DETAILS_VOLUME_MODE.BIG:
          retValue = "大";
          break;
        default:
          break;
      }
      return retValue;
    };
    // クイック操作ボタンよりも前の項目
    const setConfirmItemList = (settingList: FridgeDetailsSetSettingsList) => {
      let index = 0;
      const list: Array<FridgeDetailsDisplaySettingsListItem> = [];

      // パーシャル／チルド
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM) && settingList.partial_mode != undefined && settingList.partial_mode != null) {
        const item = { index: index, itemName: "パーシャル／チルド", value: "" }
        switch (settingList.partial_mode) {
          case FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.CHILLED:
            item.value = "チルド";
            break;
          case FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.PARTIAL_WEAK:
            item.value = "弱"
            break;
          case FRIDGE_DETAILS_PARTIAL_CHILLED_MODE.PARTIAL_MEDIUM:
            item.value = "中"
            break;
          default:
            item.value = "強"
            break;
        }
        list.push(item);
        index ++;
      }

      // パーシャル半解凍
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALHALFDEFROST) && settingList.partial_half_defrost_mode != undefined && settingList.partial_half_defrost_mode != null) {
        const item = { index: index, itemName: "パーシャル半解凍", value: "" }
        switch (settingList.partial_half_defrost_mode) {
          case FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.OFF:
            item.value = "切";
            break;
          case FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.SEVENHOUR:
            item.value = "7時間"
            break;
          case FRIDGE_DETAILS_PARTIAL_HALF_DEFROST.FIVEHOUR:
            item.value = "5時間"
            break;
          default:
            item.value = "切"
            break;
        }
        list.push(item);
        index ++;
      }

      // 冷蔵
      NativeClient.sendDebugLog("FridgeSettingsPage setConfirmItemList() cold_room_mode = " + settingList.cold_room_mode);
      const coldRoomItem = { index: index, itemName: "冷蔵", value: createSettingItemValueStrength(settingList.cold_room_mode!) };
      list.push(coldRoomItem);
      index ++;

      // 冷凍
      const freezingRoomItem = { index: index, itemName: "冷凍", value: createSettingItemValueStrength(settingList.freezing_room_mode!) };
      list.push(freezingRoomItem);
      index ++;

      // パーシャル／冷凍切替
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.SCROOMTEMPARATURE) && settingList.sc_room_temperature != undefined && settingList.sc_room_temperature != null) {
        const item = { index: index, itemName: "クーリングアシストルーム", value: "" };
        switch (settingList.sc_room_temperature) {
          case FRIDGE_DETAILS_STRENGTH.WEAK:
            item.value = "弱";
            break;
          case FRIDGE_DETAILS_STRENGTH.MEDIUM:
            item.value = "中";
            break;
          case FRIDGE_DETAILS_STRENGTH.STRONG:
            item.value = "強";
            break;
          default:
            item.value = "弱";
            break;
        }
        list.push(item);
        index ++;
      };

      // パーシャル／冷凍切替
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PARTIALFREEZINGROOM) && settingList.partial_freezing_room_mode != undefined && settingList.partial_freezing_room_mode != null) {
        const item = { index: index, itemName: "パーシャル／冷凍切替", value: "" };
        switch (settingList.partial_freezing_room_mode) {
          case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_WEAK:
            item.value = "冷凍\n弱";
            break;
          case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_MEDIUM:
            item.value = "冷凍\n中";
            break;
          case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.FREEZING_STRONG:
            item.value = "冷凍\n強";
            break;
          case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_WEAK:
            item.value = "パーシャル\n弱";
            break;
          case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_MEDIUM:
            item.value = "パーシャル\n中";
            break;
          case FRIDGE_DETAILS_PARTIAL_FROZEN_MODE.PARTIAL_STRONG:
            item.value = "パーシャル\n強";
            break;
          default:
            break;
        }
        list.push(item);
        index ++;
      };

      NativeClient.sendDebugLog("FridgeSettingsPage setConfirmItemList() list = " + JSON.stringify(list));

      confirmItemList.value = list;
    };

    // クイック操作ボタン
    const setConfirmQuickButtonItemList = (settingList: FridgeDetailsSetSettingsList) => {
      if (!checkFunction(FRIDGE_DETAILS_FUNCTION_ID.QUICKBUTTON) || settingList.quick_button_setting_list == undefined) {
        confirmQuickButtonItemList.value = [];
        return;
      }

      let index = 0;
      const list: Array<FridgeDetailsDisplaySettingsListItem> = [];

      for (const item of settingList.quick_button_setting_list) {
        let itemName = "";
        let itemValue = "";
        switch (item.mode) {
          case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
            itemName = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.QUENCH;
            itemValue = `${item.time}分${secondsZeroPadding(item.time_second)}秒`;
            break;
          case FRIDGE_DETAILS_COOLING_MODE.COLD:
            itemName = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.COLD;
            itemValue = `${item.time}分`;
            break;
          case FRIDGE_DETAILS_COOLING_MODE.FROZEN:
            itemName = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.FROZEN;
            itemValue = `${item.time}分`;
            break;
          default:
            break;
        }
        const element = { index: index, itemName: itemName, value: itemValue };
        list.push(element);
        index ++;
      }
      confirmQuickButtonItemList.value = list;
    };

    // クイック操作ボタン以降に表示する項目
    const setConfirmAfterQuickButtonItemList = (settingList: FridgeDetailsSetSettingsList) => {
      let index = 0;
      const list: Array<FridgeDetailsDisplaySettingsListItem> = [];

      // 新鮮凍結
      if (checkFunction(FRIDGE_DETAILS_FUNCTION_ID.FRESHFROZEN) && settingList.fresh_frozen_status != undefined && settingList.fresh_frozen_status != null) {
        const item = { index: index, itemName: "新鮮凍結", value: createSettingItemValueOnOff(settingList.fresh_frozen_status!) };
        list.push(item);
        index ++;
      }

      // 速氷
      const fastIceItem = { index: index, itemName: "速氷", value: createSettingItemValueOnOff(settingList.fast_ice_status!) };
      list.push(fastIceItem);
      index ++;

      // 製氷停止
      const stopIceItem = { index: index, itemName: "製氷停止", value: createSettingItemValueOnOff(settingList.stop_ice_status!) };
      list.push(stopIceItem);
      index ++;

      if(checkFunction(FRIDGE_DETAILS_FUNCTION_ID.ECONAVILEDONOFF)) {
        let itemValue = false;
        if (settingList.econavi_lamp_status != undefined && settingList.econavi_lamp_status != null) {
          itemValue = settingList.econavi_lamp_status;
        }
        const item = { index: index, itemName: "「エコナビ」ランプ表示", value: createSettingItemValueOnOff(itemValue) };
        NativeClient.sendDebugLog("econavi_lamp_status = " + settingList.econavi_lamp_status! + ", item = " + JSON.stringify(item));
        list.push(item);
        index ++;
      }

      if(checkFunction(FRIDGE_DETAILS_FUNCTION_ID.COOLOVEN)) {
        const item = { index: index, itemName: "「クーリングアシスト」ランプ表示", value: createSettingItemBrightness(settingList.cooloven_lamp_mode!) };
        list.push(item);
        index ++;
      }
      const itemDoorAlarm = { index: index, itemName: "ドアアラーム音量", value: createSettingItemVolume(settingList.door_alarms_mode!) };
      list.push(itemDoorAlarm);
      index ++;
  
      const itemColdroomLight = { index: index, itemName: "冷蔵室庫内照明", value: createSettingItemBrightness(settingList.coldroom_light_mode!) };
      list.push(itemColdroomLight);
      index ++;
      
      if(checkFunction(FRIDGE_DETAILS_FUNCTION_ID.PCROOMLIGHTMODE)) {
        const item = { index: index, itemName: "パーシャル/チルド切替室照明", value: createSettingItemBrightness(settingList.pcroom_light_mode!) };
        list.push(item);
        index ++;
      }

      confirmAfterQuickButtonItemList.value = list;
    };

    // 設定値確認ダイアログ表示
    const displaySettingConfirmDialog = (currentSettings: FridgeDetailsSetSettingsList, isInitial: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage displaySettingConfirmDialog()");
      if (currentSettings != null) {
        setConfirmItemList(currentSettings);
        setConfirmQuickButtonItemList(currentSettings);
        setConfirmAfterQuickButtonItemList(currentSettings);
        if (isInitial) {
          confirmTitle.value = "初期設定確認";
        } else {
          confirmTitle.value = "設定確認";
        }
        confirmInitial.value = isInitial;
        isShowConfirm.value = true;
      }
    };

    //
    // 警告／免責ダイアログ表示
    //
    const isShowCaution = ref(false);
    const cautionDialogType = ref(0);
    const cautionDialogTitle = ref("");
    const cautionDialogMessage = ref("");
    const cautionImage = ref("");
    const cautionMessageAlign = ref(0);
    const cautionDialogCancelTitle = "キャンセル";
    const cautionDialogOKButtonTitle = "OK";
    let cautionInitial = false;
    const cautionDialogCancelHandler = () => {
      isShowCaution.value = false;
      cautionInitial = false;
    };
    const cautionDialogOKHandler = () => {
      isShowCaution.value = false;
      let currentSettings: FridgeDetailsSetSettingsList | null = null;
      if (cautionInitial) {
        currentSettings = store.getters["fridgeDetails/getSettingsInitialValue"];
      } else {
        currentSettings = copyObject(settingsBuffer.value);
      }
      if (currentSettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage cautionDialogOKHandler() currentSettings = null");
        return;
      }
      displaySettingConfirmDialog(currentSettings, cautionInitial);
      cautionInitial = false;
    };
    const displayCautionDialog = (isInitial: boolean) => {
      cautionDialogType.value = 18;
      cautionDialogTitle.value = "";
      cautionDialogMessage.value = "温度設定を変更すると停電そなえモードが終了します。\n設定してよろしいですか？";
      cautionImage.value = "";
      cautionMessageAlign.value = 1;
      cautionInitial = isInitial;
      isShowCaution.value = true;
    };
    const displayDisclaimerDialog = (isInitial: boolean) => {
      cautionDialogType.value = 17;
      cautionDialogTitle.value = "停電そなえモード中です";
      cautionDialogMessage.value = "現在、停電そなえモード中です。\n\n設定を切り替えると停電そなえモードは一時的に待機状態となりますがよろしいですか？\n※停電後の保冷時間が短くなる場合があります。";
      cautionImage.value = require("@/assets/images/image_popup_outage_disclaimer.png");
      cautionMessageAlign.value = 1;
      cautionInitial = isInitial;
      isShowCaution.value = true;
    };
    const displayNochangeDialog = () => {
      cautionDialogType.value = 13;
      cautionDialogTitle.value = "";
      cautionDialogMessage.value = "設定が変更されていません";
      cautionImage.value = "";
      cautionMessageAlign.value = 1;
      cautionInitial = false;
      isShowCaution.value = true;
    };

    //
    // 送信関連ボタン処理
    //
    const commonSendProcess = (isInitial: boolean) => {
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() initial = " + isInitial);
      let currentSettings: FridgeDetailsSetSettingsList | null = null;
      if (isInitial) {
        currentSettings = store.getters["fridgeDetails/getSettingsInitialValue"];
      } else {
        NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() settingsBuffer = " + JSON.stringify(settingsBuffer.value));
        currentSettings = copyObject(settingsBuffer.value);
      }
      if (currentSettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() currentSettings is null");
      }
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() after set currentSettings = " + JSON.stringify(currentSettings));
      const sendData = selectChangeValues(currentSettings!);
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() after selectChangeValues currentSettings = " + JSON.stringify(currentSettings));
      if (JSON.stringify(sendData) == "{}") {
        NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() no change");
        displayNochangeDialog();
        return;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() after if initial settingsBuffer = " + JSON.stringify(settingsBuffer.value));
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() before if null currentSettings = " + JSON.stringify(currentSettings));
      if (currentSettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() currentSettings = null");
        return;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() before arrange currentSettings = " + JSON.stringify(currentSettings));
      const displaySettings = arrangeSettingData(currentSettings);
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() after arrange currentSettings = " + JSON.stringify(currentSettings));
      if (displaySettings == null) {
        NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() arrangeSettingData is null");
        return;
      }
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() currentSettings = " + JSON.stringify(currentSettings));
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() displaySettings = " + JSON.stringify(displaySettings));
      const checkResult = checkSettings(displaySettings);
      NativeClient.sendDebugLog("FridgeSettingsPage commonSendProcess() checkResult = " + checkResult);
      switch (checkResult) {
        case 1:
          // 設定値一覧ダイアログ表示
          displaySettingConfirmDialog(currentSettings, isInitial);
          break;
        case 2:
          // 温度設定確認ダイアログを表示
          displayCautionDialog(isInitial);
          break;
        case 3:
          // 停電そなえモードOFFの注意喚起ダイアログ表示
          displayDisclaimerDialog(isInitial);
          break;
        default:
          break;
      }
    };

    const initialButtonAction = () => {
      NativeClient.sendDebugLog("FridgeSettingsPage initialButtonAction()");
      commonSendProcess(true);
    };

    // 送信ボタンのタップ
    const sendButtonAction = () => {
      NativeClient.sendDebugLog("FridgeSettingsPage sendButtonAction()");
      commonSendProcess(false);
    };

    //
    // ヘルプ表示ダイアログ
    //
    const isShowHelp = ref(false);
    const helpType = ref("");
    const productCode = computed(() => {
      const apiParam = store.getters["common/getApiBaseParams"];
      if (apiParam != null && apiParam.appliance != null && apiParam.appliance.length > 0) {
        return apiParam.appliance;
      }
      return "";
    });
    const closeButtonHandler = () => {
      isShowHelp.value = false;
    };
    const partialChilledChangeHelp = () => {
      helpType.value = "S101P";
      isShowHelp.value = true;
    };
    const refrigerationHelp = () => {
      helpType.value = "S201P";
      isShowHelp.value = true;
    };
    const frozenHelp = () => {
      helpType.value = "S202P";
      isShowHelp.value = true;
    };
    const freshFrozenHelp = () => {
      helpType.value = "S206P";
      isShowHelp.value = true;
    };
    const partialFrozenModeHelp = () => {
      helpType.value = "S103P";
      isShowHelp.value = true;
    };
    const quickIcingHelp = () => {
      helpType.value = "S203P";
      isShowHelp.value = true;
    };
    const quickOperationButtonHelp = () => {
      helpType.value = "S102P";
      isShowHelp.value = true;
    };
    const detailSettingHelp = () => {
      helpType.value = "S205P";
      isShowHelp.value = true;
    };
    const partialHalfHelp = () => {
      helpType.value = "S207P";
      isShowHelp.value = true;
    };
 
    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const typeApiError = computed(() => {
      NativeClient.sendDebugLog("FridgeSettingsPage typeApiError() refAPIDialogInfo.value.type = " + refAPIDialogInfo.value.type);
      // if (refAPIDialogInfo.value.type == 2) {
      //   return 9;
      // }
      // return 10;
      return 9;
    });
    const apiErrorTitle = computed(() => {
      if (refAPIDialogInfo.value.type == 2) {
        return "送信できませんでした";
      }
      if (getApiErrorTitle != undefined && getApiErrorTitle != null) {
        return getApiErrorTitle.value;
      }
      return "";
    });
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // if (refAPIDialogInfo.value.positiveCallback != null) {
      //   refAPIDialogInfo.value.positiveCallback();
      // }
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      updateDisplayKey,
      isValidSettings,
      scroll,
      settingsBuffer,
      isValidPartialChilledChange,
      partialChilled,
      partialChilledTitleText,
      initialIndexPartialChilled,
      partialChilledChange,
      partialChilledRadioButtonLabels,
      partialText,
      initialIndexPartial,
      partialChange,
      strengthRadioButtonLabels,
      brightnessRadioButtonLabels,
      refrigerationText,
      initialIndexRefrigeration,
      refrigerationChange,
      frozenText,
      initialIndexFrozen,
      frozenChange,
      isValidCoolingAssistStrength,
      coolingAssistRoomStrengthText,
      initialIndexCoolingAssistStrength,
      coolongAssistStrengthChange,
      freshFrozenText,
      freshFrozenNoteText,
      isValidFreshFrozen,
      changeFreshFrozen,
      isValidPartialFrozenChange,
      partialFrozenTitleText,
      partialFrozenRadioButtonLabels,
      partialFrozenStrengthText,
      initialIndexPartialFrozen,
      initialIndexPartialFrozenStrength,
      partialFrozenModeChange,
      partailFrozenStrengthChange,
      quickIcingText,
      quickIcingNoteText,
      changeQuickIcing,
      stopIceMakingText,
      changeStopIce,
      isValidQuickButton,
      quickButtonSettings,
      quickOperationButtontitleText,
      clickQuick,
      isDetailsOpen,
      detailsOpenCloseButtonText,
      clickDetailsOpenClose,
      detailSettingPlusText,
      detailSettingTitleText,
      isValidEconaviLamp,
      econaviLampTitleText,
      isValidCoolingAssitsLamp,
      coolingAssistLampTitleText,
      initialIndexCoolingAssistLamp,
      coolingAssistLampChange,
      doorAlarmVolumeTitleText,
      innerLampTitleText,
      initialIndexInnerLamp,
      innerLampChange,
      isValidPartialChilledRoomLamp,
      partialChilledLampTitleText,
      initialIndexPartialChilledLamp,
      partialChilledLampChange,
      changeEconaviLamp,
      volumeRadioButtonLabels,
      initialIndexDoorAlarmVolume,
      doorAlarmVolumeChange,
      detailSettingMinusText,
      sendButtonText,
      sendButtonAction,
      isShowConfirm,
      confirmTitle,
      confirmItemList,
      confirmQuickButtonItemList,
      confirmAfterQuickButtonItemList,
      confirmInitial,
      clickConfirmCancel,
      clickConfirmSend,
      isShowCaution,
      cautionDialogType,
      cautionDialogTitle,
      cautionDialogMessage,
      cautionImage,
      cautionMessageAlign,
      cautionDialogCancelTitle,
      cautionDialogOKButtonTitle,
      cautionDialogCancelHandler,
      cautionDialogOKHandler,
      isShowHelp,
      helpType,
      productCode,
      closeButtonHandler,
      isShowCompletion,
      completionDialogType,
      completionDialogTitle,
      completionDialogMessage,
      completionDialogButtonTitle,
      completionCloseButtonHandler,
      partialChilledChangeHelp,
      refrigerationHelp,
      frozenHelp,
      freshFrozenHelp,
      partialFrozenModeHelp,
      quickIcingHelp,
      quickOperationButtonHelp,
      detailSettingHelp,
      initialButtonText,
      initialButtonAction,
      isValidPartialHalf,
      partialHalfTitleText,
      partialHalfHelp,
      partialHalf7hourTitleText,
      partialHalf7hourSubTitleText,
      partialHalf5hourTitleText,
      partialHalf5hourSubTitleText,
      partialHalf7HourValue,
      partialHalf7HourInhibit,
      changePartialHalf7Hour,
      partialHalf5HourValue,
      partialHalf5HourInhibit,
      changePartialHalf5Hour,
      handleApiError,
      apiErrorTitle,
      //getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
